'use client'

import React, { useEffect, useRef } from 'react'
import { AppShell, AppShellMain, Stack, Title, Text, Button } from '@mantine/core'
import { useRouter } from 'next/navigation'
import ResponsiveLayout from '@/components/Layouts/ResponsiveLayout'
import { trackOffRampError } from '@/lib/analytics/questionnaireInterstitialAnalytics'

interface ErrorFallbackProps {
    title?: string
    message?: string
    showRetryButton?: boolean
    showHomeButton?: boolean
    homeButtonText?: string
    retryButtonText?: string
    onRetry?: () => void
    homeRoute?: string
    offRampApiFailure?: boolean
    skuId?: string
    /** When set, runs instead of router.replace(homeRoute) for the home button. */
    onGoHome?: () => void | Promise<void>
    /** When true, render only the centered message/actions without page shell wrappers. */
    embedded?: boolean
}

export default function ErrorFallback({
    title = 'Something went wrong',
    message = 'We&apos;re having trouble loading this page.',
    showRetryButton = true,
    showHomeButton = true,
    homeButtonText = 'Go to Home',
    retryButtonText = 'Try Again',
    onRetry,
    homeRoute = '/',
    offRampApiFailure = false,
    skuId,
    onGoHome,
    embedded = false,
}: ErrorFallbackProps) {
    const router = useRouter()
    const offRampErrorAnalyticsTrackedRef = useRef(false)

    useEffect(() => {
        if (!offRampApiFailure || offRampErrorAnalyticsTrackedRef.current) return
        offRampErrorAnalyticsTrackedRef.current = true
        trackOffRampError(skuId)
    }, [offRampApiFailure, skuId])

    const handleRetry = () => {
        if (onRetry) {
            onRetry()
        } else {
            window.location.reload()
        }
    }

    const handleGoHome = () => {
        if (onGoHome) {
            void onGoHome()
            return
        }
        router.replace(homeRoute)
    }

    const content = (
        <Stack h="70vh" align="center" justify="center" gap="md">
            <Title order={2} ta="center">{title}</Title>
            <Text ta="center" c="dimmed">
                {message}
            </Text>
            {showHomeButton && (
                <Button 
                    onClick={handleGoHome}
                    variant="filled"
                >
                    {homeButtonText}
                </Button>
            )}
            {showRetryButton && (
                <Button 
                    onClick={handleRetry}
                    variant="outline"
                >
                    {retryButtonText}
                </Button>
            )}
        </Stack>
    )

    if (embedded) {
        return content
    }

    return (
        <AppShell>
            <AppShellMain component="div">
                <ResponsiveLayout adjustForDesktop>
                    {content}
                </ResponsiveLayout>
            </AppShellMain>
        </AppShell>
    )
} 